import { useState, useImperativeHandle, forwardRef, useRef } from 'react'
import { Dropdown, type DropdownOption } from '../ui/Dropdown.js'
import type { CatalogItem } from '../../types/orders.js'
import { searchProducts } from '../../data/index'
import { formatCurrency } from '../../lib/orders-utils.js'
import { useThemeTokens } from '../../themes'

interface ItemSelectorProps {
  onItemSelect: (item: CatalogItem, quantity: number) => void
  onItemAdded?: () => void // Called after item is added, used for focus management
}

export interface ItemSelectorRef {
  clearSearchAndFocus: () => void
  getSearchInputRef: () => HTMLInputElement | null
}

export const ItemSelector = forwardRef<ItemSelectorRef, ItemSelectorProps>(
  ({ onItemSelect, onItemAdded }, ref) => {
    const [dropdownKey, setDropdownKey] = useState(0) // Key to force dropdown reset
    const theme = useThemeTokens()
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Get reference to the dropdown's internal search input
    const getSearchInput = (): HTMLInputElement | null => {
      return dropdownRef.current?.querySelector('input[type="text"]') || null
    }

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      clearSearchAndFocus: () => {
        // Force dropdown to reset and clear search by changing its key
        setDropdownKey(prev => prev + 1)
        // Focus the search input after reset
        setTimeout(() => {
          const input = getSearchInput()
          input?.focus()
        }, 0)
      },
      getSearchInputRef: getSearchInput
    }))

    // Convert catalog items to dropdown options
    const getItemOptions = (searchTerm: string): DropdownOption[] => {
      if (searchTerm.length < 2) return []
      
      const items = searchProducts(searchTerm).slice(0, 10) // Limit to 10 results
      return items.map(item => ({
        id: item.id,
        label: item.name,
        value: item,
        description: `${item.category} • ${formatCurrency(item.pricePerUnit)} per ${item.unit} • ${item.qtyPerUnit} per ${item.unit}`,
        metadata: {
          category: item.category,
          price: item.pricePerUnit,
          perItem: item.perItem,
          qtyPerUnit: item.qtyPerUnit,
          unit: item.unit,
          description: item.description,
          source: item.source
        }
      }))
    }

    // Custom filter function for item search
    const filterItems = (_options: DropdownOption[], searchTerm: string): DropdownOption[] => {
      return getItemOptions(searchTerm)
    }

    // Custom render function for item options
    const renderItemOption = (option: DropdownOption, isHighlighted: boolean) => {
      const item = option.value as CatalogItem
      return (
        <div 
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            backgroundColor: isHighlighted ? theme.colors.primary + '10' : 'transparent',
            borderBottom: `1px solid ${theme.colors.border}`,
            transition: 'all 0.15s ease',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
            {/* Product Image */}
            <div style={{ flexShrink: 0 }}>
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'cover',
                    borderRadius: '6px',
                    backgroundColor: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`
                  }}
                  onError={(e) => {
                    // Hide broken images and show fallback
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              ) : (
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: theme.colors.surface,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: theme.colors.textSecondary,
                  textAlign: 'center'
                }}>
                  {item.isLegacyProduct ? 'Legacy' : 'No Image'}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontWeight: theme.typography.fontWeight.medium, 
                fontSize: theme.typography.fontSize.sm, 
                color: theme.colors.text,
                marginBottom: '4px'
              }}>
                {item.name}
                {item.isLegacyProduct && (
                  <span style={{ 
                    marginLeft: '8px',
                    fontSize: '10px',
                    backgroundColor: theme.colors.warning,
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontWeight: 'normal'
                  }}>
                    LEGACY
                  </span>
                )}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: theme.colors.textSecondary,
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.description}
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: theme.colors.textSecondary,
                marginBottom: '2px'
              }}>
                Category: {item.category} • Qty per {item.unit}: {item.qtyPerUnit}
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: theme.colors.textSecondary
              }}>
                Source: {item.source} {item.orders > 0 && `• Ordered ${item.orders} times`}
              </div>
            </div>
            
            {/* Price Info */}
            <div style={{ textAlign: 'right', marginLeft: '16px', flexShrink: 0 }}>
              <div style={{ 
                fontSize: theme.typography.fontSize.sm, 
                fontWeight: theme.typography.fontWeight.medium, 
                color: theme.colors.text,
                marginBottom: '2px'
              }}>
                {formatCurrency(item.pricePerUnit)}
              </div>
              <div style={{ 
                fontSize: '11px', 
                color: theme.colors.textSecondary,
                marginBottom: '2px'
              }}>
                per {item.unit}
              </div>
              <div style={{ 
                fontSize: '10px', 
                color: theme.colors.textSecondary
              }}>
                {formatCurrency(item.perItem)} per item
              </div>
            </div>
          </div>
        </div>
      )
    }

    const handleItemSelect = (option: DropdownOption) => {
      const item = option.value as CatalogItem
      // Immediately add item with quantity 1
      onItemSelect(item, 1)
      // Clear search and reset dropdown
      setDropdownKey(prev => prev + 1)
      // Notify parent that item was added (for focus management)
      onItemAdded?.()
    }

    return (
      <div style={{ width: '100%' }} ref={dropdownRef}>
        <Dropdown
          key={dropdownKey}
          options={[]}
          value={null}
          onSelect={handleItemSelect}
          placeholder="Type to search items... (e.g., 'tape', 'gloves')"
          label="Add Item"
          searchable
          renderOption={renderItemOption}
          filterFunction={filterItems}
        />
      </div>
    )
  }
) 